import { Component, ElementRef, EventEmitter, Input, Output, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { DwMediaModalService } from '@devwareapps/devware-cap';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppearancePresetsService } from '../../services/appearance/appearance-presets.service';
import { AppearanceSettings, resolveAppearance, ResolvedAppearance } from '../../services/appearance/appearance';
import { AppearancePresetEntity } from '../../../../meta-data/app-meta-data.service';

/** CAP owns image uploads; callers supply identity and persist the staged image selection. */
@Component({ selector: 'app-avatar-banner',
    templateUrl: './avatar-banner.component.html', styleUrls: ['./avatar-banner.component.scss'] })
export class AvatarBannerComponent implements OnDestroy, OnChanges {
    constructor(public presets: AppearancePresetsService, private _mediaModal: DwMediaModalService) {}
    @Input() saveImages: (avatarId: number, bannerId: number) => Observable<void>;
    @Input() imageOwnerKey: string;
    imageEditing = false;
    imageBusy = false;
    imageError = '';
    private _avatarId: number;
    private _bannerId: number;
    private _requests = new Subscription();
    @ViewChild('imageFile') imageFile: ElementRef<HTMLInputElement>;
    private _selectingAvatar = false;
    beginImages(): void {
        if (!this.editable || this.imageBusy) { return; }
        this._avatarId = this.avatarMediaId; this._bannerId = this.bannerMediaId;
        this.imageError = ''; this.imageEditing = true;
    }
    selectImage(avatar: boolean): void {
        if (!this.editable || !this.imageEditing || this.imageBusy) { return; }
        this._selectingAvatar = avatar;
        // Match dw-media-selector: choose the file before creating CAP's crop/upload modal.
        const input = this.imageFile.nativeElement;
        input.value = '';
        input.click();
    }
    imageSelected(files: FileList): void {
        if (!files?.length || !this.editable || !this.imageEditing || this.imageBusy) { return; }
        const avatar = this._selectingAvatar;
        this.imageBusy = true; this.imageError = '';
        this._requests.add(this._mediaModal.showSelectFileModal({
            isSecure: false,
            allowedFileTypes: '.jpg,.jpeg,.png,.gif,.webp,.bmp',
            imageResizeOptions: avatar
                ? {cropWidth: 250, cropHeight: 250, outputWidth: 250, outputHeight: 250, cropRound: true, maintainAspectRatio: true, allowResize: true}
                : {cropWidth: 550, cropHeight: 138, outputWidth: 2400, outputHeight: 600, cropRound: false, allowResize: true},
        }, files).pipe(take(1)).subscribe(media => {
            this.imageBusy = false;
            if (media) { if (avatar) { this._avatarId = media.DwroMedia.MediaId; } else { this._bannerId = media.DwroMedia.MediaId; } }
        }, () => { this.imageBusy = false; this.imageError = 'Unable to upload your image. Please try again.'; }, () => this.imageBusy = false));
    }
    clearImages(): void { this._avatarId = null; this._bannerId = null; }
    cancelImages(): void { this.imageEditing = false; this.imageError = ''; }
    commitImages(): void {
        if (!this.editable || !this.imageEditing || this.imageBusy || !this.saveImages) { return; }
        this.imageBusy = true; this.imageError = '';
        this._requests.add(this.saveImages(this._avatarId, this._bannerId).pipe(take(1)).subscribe(() => {
            this.avatarMediaId = this._avatarId; this.bannerMediaId = this._bannerId;
            this.imageBusy = false; this.imageEditing = false;
        }, () => { this.imageBusy = false; this.imageError = 'Unable to save your images. Please try again.'; }));
    }
    ngOnDestroy(): void { this._requests.unsubscribe(); }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.imageOwnerKey || (changes.editable && !this.editable)) {
            this._requests.unsubscribe(); this._requests = new Subscription();
            this.imageEditing = false; this.imageBusy = false; this.imageError = '';
        }
    }
    @Output() styleRequested = new EventEmitter<void>();
    @Input() resolvedDisplay = false;
    @Input() appearance: AppearanceSettings;
    @Input() name = '';
    @Input() subtitle = '';
    @Input() avatarMediaId: number;
    @Input() bannerMediaId: number;
    @Input() profileLink: string;
    @Input() mode: 'compact' | 'normal' | 'full' = 'normal';
    @Input() fitName = false;
    @Input() avatarOnly = false;
    @Input() editable = false;
    resolve(presets: AppearancePresetEntity[]): ResolvedAppearance {
        return resolveAppearance(this.appearance, presets?.find(p => p.AppearancePresetId === this.appearance?.AppearancePresetId), this.imageEditing ? this._avatarId : this.avatarMediaId, this.imageEditing ? this._bannerId : this.bannerMediaId);
    }
    get initials(): string {
        const parts = (this.name || '').trim().split(/\s+/).filter(Boolean);
        return parts.length ? (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() : 'S';
    }
}
